import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { Modal, Button, Group, Stack, Text, Checkbox, Alert, Loader, Divider } from "@mantine/core";
import {
    INITIATE_POSITION_CLOSE,
    ABANDON_POSITION_CLOSE,
    COMPLETE_POSITION_CLOSE,
} from "../../queries/waves";
import { getExitFlowType } from "../WaveConfigContext";

/* ============================================================================
 * Wave E — Obstruction close flow.
 *
 * Rendered when an enrolled Phase 2/3 participant taps "Cover" on a position.
 * Lifecycle:
 *   open → initiatePositionClose (server assigns attempt_id + arm)
 *        → 4-step or single-step UI (from arm/phase)
 *        → completePositionClose  → onComplete()  (parent then covers)
 *        or abandonPositionClose  → onAbandon()   (parent does nothing)
 *   120s with no completion → auto-abandon.
 *
 * The backend has no server-side 120s timer — this component enforces it.
 * ==========================================================================*/

const CLOSE_TIMEOUT_MS = 120_000;
const FORCED_REVIEW_SECONDS = 10;

const money = (n) => {
    const v = Number(n) || 0;
    const sign = v >= 0 ? "+$" : "-$";
    return `${sign}${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function ObstructionCloseModal({
    opened,
    position,
    waveConfig,
    onComplete,     // () => void  — proceed to actually cover the position
    onAbandon,      // () => void  — close modal without covering
    onFallbackClose // () => void  — initiate failed recoverably → do a normal cover
}) {
    // "initiating" | "flow" | "error"
    const [stage, setStage] = useState("initiating");
    const [attemptId, setAttemptId] = useState(null);
    const [flowType, setFlowType] = useState("four_step");
    const [step, setStep] = useState(1);          // four-step: 1..4
    const [riskAck, setRiskAck] = useState(false);
    const [countdown, setCountdown] = useState(FORCED_REVIEW_SECONDS);
    const [errorMsg, setErrorMsg] = useState(null);
    const [busy, setBusy] = useState(false);

    const timerRef = useRef(null);
    const finalisedRef = useRef(false); // guards against double abandon/complete

    // Live mirrors so the 120s auto-abandon closure (captured at initiate time)
    // reports the CURRENT step/flow, not a stale one. See item B / CHANGE 3.
    const stepRef = useRef(1);
    const flowTypeRef = useRef("four_step");
    stepRef.current = step;
    flowTypeRef.current = flowType;

    const [initiate] = useMutation(INITIATE_POSITION_CLOSE);
    const [abandon] = useMutation(ABANDON_POSITION_CLOSE);
    const [complete] = useMutation(COMPLETE_POSITION_CLOSE);

    const metrics = position?.metrics || {};
    // The value the participant sees on screen at this moment (guide 5h).
    const currentPnl = Number(metrics.grossPnl) || 0;

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    // --- finalisers -------------------------------------------------------
    const doAbandon = useCallback(async () => {
        if (finalisedRef.current) { onAbandon(); return; }
        finalisedRef.current = true;
        clearTimer();
        if (attemptId) {
            // Highest step (1–4) reached in the four-step flow; omit for Arm B
            // single-step closes (no multi-step flow to measure dropout on).
            const closeFlowStepReached = flowTypeRef.current === "four_step" ? stepRef.current : null;
            try {
                await abandon({ variables: {
                    attemptId,
                    pnlAtAbandonment: Number(currentPnl.toFixed(2)),
                    closeFlowStepReached,
                } });
            } catch (_) { /* already finalised server-side — ignore */ }
        }
        onAbandon();
    }, [attemptId, abandon, currentPnl, onAbandon]);

    const doComplete = useCallback(async () => {
        if (finalisedRef.current) return;
        finalisedRef.current = true;
        clearTimer();
        setBusy(true);
        try {
            await complete({ variables: { attemptId, pnlAtClose: Number(currentPnl.toFixed(2)) } });
        } catch (_) { /* already finalised — proceed to cover anyway */ }
        setBusy(false);
        onComplete();
    }, [attemptId, complete, currentPnl, onComplete]);

    // --- kick off the attempt when the modal opens ------------------------
    useEffect(() => {
        if (!opened || !position) return;
        let cancelled = false;

        // reset local state for a fresh attempt
        finalisedRef.current = false;
        setStage("initiating");
        setStep(1);
        setRiskAck(false);
        setCountdown(FORCED_REVIEW_SECONDS);
        setErrorMsg(null);

        (async () => {
            try {
                const { data } = await initiate({ variables: { positionId: String(position.id) } });
                if (cancelled) return;
                const res = data.initiatePositionClose;
                setAttemptId(res.attempt_id);
                // Server arm takes priority over cached config (guide 5b).
                const serverConfig = { ...waveConfig, currentArm: res.intervention_arm ?? waveConfig?.currentArm };
                setFlowType(getExitFlowType(serverConfig));
                setStage("flow");

                // Start the 120s auto-abandon timer.
                clearTimer();
                timerRef.current = setTimeout(() => { doAbandon(); }, CLOSE_TIMEOUT_MS);
            } catch (err) {
                if (cancelled) return;
                // Between session windows in Phase 3 — fall back to a normal cover
                // rather than blocking the participant (guide 5i / error table).
                if (err.message.includes("No active session")) {
                    onFallbackClose();
                    return;
                }
                setErrorMsg(err.message);
                setStage("error");
            }
        })();

        return () => { cancelled = true; clearTimer(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, position?.id]);

    // --- step 4 forced-review countdown -----------------------------------
    useEffect(() => {
        if (stage !== "flow" || flowType !== "four_step" || step !== 4) return;
        setCountdown(FORCED_REVIEW_SECONDS);
        const iv = setInterval(() => {
            setCountdown((c) => {
                if (c <= 1) { clearInterval(iv); return 0; }
                return c - 1;
            });
        }, 1000);
        return () => clearInterval(iv);
    }, [stage, flowType, step]);

    const posLabel = position
        ? `${metrics.quantity ?? position.quantity} ${position.symbol}`
        : "";
    const exitPrice = metrics.currentPrice;

    // Prevent closing via backdrop/esc from silently skipping the abandon log.
    const handleModalClose = () => { doAbandon(); };

    return (
        <Modal
            opened={opened}
            onClose={handleModalClose}
            title="Close Position"
            centered
            closeOnClickOutside={false}
            withCloseButton={false}
        >
            {stage === "initiating" && (
                <Group position="center" py="lg">
                    <Loader size="sm" /><Text>Preparing your close request…</Text>
                </Group>
            )}

            {stage === "error" && (
                <Stack>
                    <Alert color="red" title="Unable to close right now">{errorMsg}</Alert>
                    <Group position="right">
                        <Button variant="default" onClick={onAbandon}>Dismiss</Button>
                    </Group>
                </Stack>
            )}

            {stage === "flow" && flowType === "single_step" && (
                <Stack>
                    <Text weight={600}>Confirm Close</Text>
                    <Divider />
                    <Row label="Exit price" value={exitPrice ? money(exitPrice).replace("+", "") : "-"} />
                    <Row label="Quantity" value={posLabel} />
                    <Row label="P&L" value={money(currentPnl)} valueColor={currentPnl >= 0 ? "green" : "red"} />
                    <Group position="apart" mt="md">
                        <Button variant="default" onClick={doAbandon} disabled={busy}>Cancel</Button>
                        <Button color="blue" onClick={doComplete} loading={busy}>Confirm Close</Button>
                    </Group>
                </Stack>
            )}

            {stage === "flow" && flowType === "four_step" && (
                <Stack>
                    <Text size="sm" color="dimmed">Step {step} of 4</Text>

                    {step === 1 && (
                        <>
                            <Text weight={600}>Review Your Position</Text>
                            <Divider />
                            <Row label="Current P&L"
                                 value={money(currentPnl)}
                                 valueColor={currentPnl >= 0 ? "green" : "red"} />
                            {metrics.pnlPercent != null && (
                                <Row label="Return" value={`${metrics.pnlPercent >= 0 ? "+" : ""}${Number(metrics.pnlPercent).toFixed(2)}%`} />
                            )}
                            <Group position="apart" mt="md">
                                <Button variant="default" onClick={doAbandon}>Cancel</Button>
                                <Button onClick={() => setStep(2)}>Continue →</Button>
                            </Group>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <Text weight={600}>Market Risk</Text>
                            <Divider />
                            <Checkbox
                                checked={riskAck}
                                onChange={(e) => setRiskAck(e.currentTarget.checked)}
                                label="I understand my position may be at a loss and that market conditions can change."
                            />
                            <Group position="apart" mt="md">
                                <Button variant="default" onClick={doAbandon}>Cancel</Button>
                                <Button disabled={!riskAck} onClick={() => setStep(3)}>Next →</Button>
                            </Group>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <Text weight={600}>Confirm Exit</Text>
                            <Divider />
                            <Row label="Exit price" value={exitPrice ? money(exitPrice).replace("+", "") : "-"} />
                            <Row label="Quantity" value={posLabel} />
                            <Row label="P&L" value={money(currentPnl)} valueColor={currentPnl >= 0 ? "green" : "red"} />
                            <Group position="apart" mt="md">
                                <Button variant="default" onClick={doAbandon}>Cancel</Button>
                                <Button onClick={() => setStep(4)}>Confirm Exit →</Button>
                            </Group>
                        </>
                    )}

                    {step === 4 && (
                        <>
                            <Text weight={600}>Final Review</Text>
                            <Divider />
                            <Text size="sm">Your exit has been submitted. Please review for {FORCED_REVIEW_SECONDS} seconds.</Text>
                            <Text align="center" size="lg" weight={700} my="sm">
                                {countdown > 0 ? `⏱ ${countdown} second${countdown === 1 ? "" : "s"} remaining…` : "Ready"}
                            </Text>
                            <Group position="apart" mt="md">
                                <Button variant="default" onClick={doAbandon} disabled={busy}>Cancel</Button>
                                <Button color="blue" disabled={countdown > 0 || busy} loading={busy} onClick={doComplete}>
                                    Submit
                                </Button>
                            </Group>
                        </>
                    )}
                </Stack>
            )}
        </Modal>
    );
}

function Row({ label, value, valueColor }) {
    return (
        <Group position="apart">
            <Text color="dimmed" size="sm">{label}</Text>
            <Text weight={500} color={valueColor}>{value}</Text>
        </Group>
    );
}
